import {
    Control,
    Controller,
    FieldValues,
    Path,
    RegisterOptions,
} from 'react-hook-form';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Tooltip,
    Typography,
} from '@mui/material';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import React, { type JSX } from 'react';
import { Localized } from '../../../../lib/Localization.tsx';

export interface ReactHookFormSelectOption<FieldType> {
    value: FieldType;
    display: React.ReactNode;
    disabled?: boolean;
    tooltip?: JSX.Element;
}

export interface ReactHookFormSelectProps<
    FormFields extends FieldValues,
    FieldType,
    TContext extends object = object,
> {
    label: React.ReactNode;
    control: Control<FormFields, TContext>;
    name: Path<FormFields>;
    options: ReactHookFormSelectOption<FieldType | ''>[];
    style?: React.CSSProperties;
    itemStyle?: SxProps<Theme>;
    readonly?: boolean;
    rules?: Omit<
        RegisterOptions<FormFields, Path<FormFields>>,
        'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >;
}

export function ReactHookFormDropdownSelect<
    FormFields extends FieldValues,
    FieldType extends number | string | string[],
    TContext extends object = object,
>({
    label,
    control,
    name,
    options,
    style,
    itemStyle,
    readonly,
    rules,
}: ReactHookFormSelectProps<FormFields, FieldType, TContext>) {
    return (
        <FormControl
            style={{
                width: '100%',
                ...style,
            }}
        >
            <InputLabel disableAnimation={readonly} variant="standard">
                {label}
            </InputLabel>
            <Controller<FormFields>
                name={name}
                control={control}
                rules={rules}
                render={({ field, fieldState: { error, isTouched } }) => (
                    <>
                        <Select
                            name={field.name}
                            value={field.value}
                            error={!!error && !isTouched}
                            disabled={readonly}
                            onChange={(e, child) => {
                                // check if disabled option was clicked
                                if (
                                    React.isValidElement(child) &&
                                    (child.props as { disabled: boolean })
                                        .disabled
                                ) {
                                    return;
                                }

                                field.onChange(e as React.ChangeEvent);
                            }}
                        >
                            {options.map(o => {
                                return (
                                    <MenuItem
                                        value={o.value}
                                        key={o.value.toString()}
                                        disabled={o.disabled}
                                        sx={itemStyle}
                                    >
                                        {o.tooltip ? (
                                            <Tooltip
                                                key={o.value.toString()}
                                                title={o.tooltip}
                                                placement="right"
                                                style={{
                                                    width: '100%',
                                                    pointerEvents: 'auto',
                                                }}
                                            >
                                                <span>{o.display}</span>
                                            </Tooltip>
                                        ) : (
                                            <>{o.display}</>
                                        )}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        {error && !isTouched && (
                            <FormHelperText>{error.message}</FormHelperText>
                        )}
                    </>
                )}
            />
        </FormControl>
    );
}

export enum MultiSelectEntryType {
    ITEM = 'ITEM',
    GROUP = 'GROUP',
}

type MultiSelectOption<FieldType> = {
    type: MultiSelectEntryType.ITEM;
    value: FieldType;
    display: React.ReactNode;
};

type MultiSelectGroup<FieldType> = {
    type: MultiSelectEntryType.GROUP;
    label: string;
    options: MultiSelectOption<FieldType>[];
};

type ReactHookFormMultiSelectOption<FieldType> =
    | MultiSelectOption<FieldType>
    | MultiSelectGroup<FieldType>;

export interface ReactHookFormMultiSelectProps<
    FormFields extends FieldValues,
    FieldType,
    TContext extends object = object,
> extends Omit<
    ReactHookFormSelectProps<FormFields, FieldType, TContext>,
    'options'
> {
    options: ReactHookFormMultiSelectOption<FieldType>[];
    infoBox?: React.ReactNode;
}

export function ReactHookFormDropdownMultiSelect<
    FormFields extends FieldValues,
    FieldType extends number | string | string[],
    TContext extends object = object,
>({
    label,
    control,
    name,
    options,
    style,
    itemStyle,
    readonly,
    rules,
    infoBox,
}: ReactHookFormMultiSelectProps<FormFields, FieldType, TContext>) {
    const allOptions = fetchAllOptionValues(options);
    const toggleValue = (value: FieldType, current: FieldType[]) => {
        return current.includes(value)
            ? current.filter(v => v !== value)
            : [...current, value];
    };
    const toggleGroupValues = (
        group: MultiSelectGroup<FieldType>,
        current: FieldType[],
    ) => {
        const allSelected = group.options.every(o => current.includes(o.value));
        if (allSelected) {
            return current.filter(v => !group.options.some(o => o.value === v));
        }
        const groupValues = group.options.map(o => o.value);
        return [...new Set([...current, ...groupValues])];
    };
    return (
        <FormControl
            sx={{
                width: '100%',
                ...style,
            }}
        >
            <InputLabel disableAnimation={readonly} variant="standard">
                {label}
            </InputLabel>
            <Controller<FormFields>
                name={name}
                control={control}
                rules={rules}
                render={({ field, fieldState: { error, isTouched } }) => {
                    const isAllSelected =
                        allOptions.length > 0 &&
                        allOptions.every(val => field.value.includes(val));
                    const handleSelectAll = () => {
                        if (isAllSelected) {
                            field.onChange([]);
                        } else {
                            field.onChange(allOptions);
                        }
                    };

                    return (
                        <>
                            <Select
                                multiple
                                displayEmpty
                                name={field.name}
                                value={field.value}
                                error={!!error && !isTouched}
                                disabled={readonly}
                                renderValue={selected => {
                                    const selectedCount = selected.filter(
                                        (s: FieldType) =>
                                            allOptions.includes(s),
                                    ).length;
                                    const totalCount = allOptions.length;
                                    if (selectedCount == 0) {
                                        return (
                                            <Localized
                                                de="Keine"
                                                fr="Aucune"
                                                it="Nessuna"
                                                en="None"
                                            />
                                        );
                                    } else if (selectedCount == totalCount) {
                                        return (
                                            <Localized
                                                de="Alle"
                                                it="Tutti"
                                                fr="Tous"
                                                en="Everyone"
                                            />
                                        );
                                    } else {
                                        return (
                                            <Localized
                                                de={`${selectedCount} von ${totalCount}`}
                                                fr={`${selectedCount} sur ${totalCount}`}
                                                it={`${selectedCount} su ${totalCount}`}
                                                en={`${selectedCount} of ${totalCount}`}
                                            />
                                        );
                                    }
                                }}
                            >
                                {infoBox && (
                                    <Box
                                        sx={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                            backgroundColor: theme =>
                                                theme.palette.background
                                                    .default,
                                            paddingX: 2,
                                            paddingY: 1,
                                            borderBottom:
                                                '1px solid rgba(0,0,0,0.12)',
                                        }}
                                    >
                                        {infoBox}
                                    </Box>
                                )}

                                <MenuItem
                                    onClick={handleSelectAll}
                                    sx={{
                                        borderBottom:
                                            '1px solid rgba(0,0,0,0.08)',
                                        display: 'flex',
                                        flexGrow: 1,
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Checkbox
                                        checked={isAllSelected}
                                        indeterminate={
                                            field.value.length > 0 &&
                                            !isAllSelected
                                        }
                                    />
                                    <Box
                                        sx={[
                                            ...(Array.isArray(itemStyle)
                                                ? itemStyle
                                                : [itemStyle]),
                                            { textTransform: 'uppercase' },
                                        ]}
                                    >
                                        <Localized
                                            de="Alle"
                                            fr="Tout"
                                            it="Tutto"
                                            en="All"
                                        />
                                    </Box>
                                </MenuItem>

                                {options.map(option => {
                                    if (
                                        option.type ===
                                        MultiSelectEntryType.GROUP
                                    ) {
                                        const groupActive =
                                            option.options.every(o =>
                                                field.value?.includes(o.value),
                                            );

                                        return (
                                            <Box key={option.label}>
                                                <MenuItem
                                                    onClick={() =>
                                                        field.onChange(
                                                            toggleGroupValues(
                                                                option,
                                                                field.value ??
                                                                    [],
                                                            ),
                                                        )
                                                    }
                                                >
                                                    <Checkbox
                                                        checked={groupActive}
                                                    />
                                                    <Box
                                                        sx={[
                                                            {
                                                                textTransform:
                                                                    'uppercase',
                                                            },
                                                            ...(Array.isArray(
                                                                itemStyle,
                                                            )
                                                                ? itemStyle
                                                                : [itemStyle]),
                                                        ]}
                                                    >
                                                        {option.label}
                                                    </Box>
                                                </MenuItem>

                                                {option.options.map(o => (
                                                    <MenuItem
                                                        key={o.value.toString()}
                                                        sx={{
                                                            paddingTop: 0,
                                                            paddingLeft: 6,
                                                            paddingBottom: 0,
                                                        }}
                                                        onClick={() =>
                                                            field.onChange(
                                                                toggleValue(
                                                                    o.value,
                                                                    field.value ??
                                                                        [],
                                                                ),
                                                            )
                                                        }
                                                    >
                                                        <Checkbox
                                                            checked={field.value?.includes(
                                                                o.value,
                                                            )}
                                                        />
                                                        <Box sx={itemStyle}>
                                                            {o.display}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Box>
                                        );
                                    }

                                    return (
                                        <MenuItem
                                            key={option.value.toString()}
                                            value={option.value}
                                            onClick={() =>
                                                field.onChange(
                                                    toggleValue(
                                                        option.value,
                                                        field.value ?? [],
                                                    ),
                                                )
                                            }
                                        >
                                            <Checkbox
                                                checked={field.value?.includes(
                                                    option.value,
                                                )}
                                            />
                                            <Box sx={itemStyle}>
                                                {option.display}
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {error && !isTouched && (
                                <FormHelperText>{error.message}</FormHelperText>
                            )}
                        </>
                    );
                }}
            />
        </FormControl>
    );
}

function fetchAllOptionValues<FieldType>(
    options: ReactHookFormMultiSelectOption<FieldType>[],
) {
    return options.flatMap(o => {
        switch (o.type) {
            case MultiSelectEntryType.ITEM:
                return [o.value];
            case MultiSelectEntryType.GROUP:
                return o.options.map(o => o.value);
        }
    });
}

export function ReactHookFormRadioSelect<
    FormFields extends FieldValues,
    FieldType extends number | string | string[],
    TContext extends object = object,
>({
    label,
    control,
    name,
    options,
    style,
    readonly,
}: ReactHookFormSelectProps<FormFields, FieldType, TContext>) {
    return (
        <FormControl
            style={{
                width: '100%',
                ...style,
            }}
        >
            <FormLabel>
                <Typography variant="overline">{label}</Typography>
            </FormLabel>
            <Controller<FormFields>
                name={name}
                control={control}
                render={({ field, fieldState: { error, isTouched } }) => (
                    <>
                        <RadioGroup
                            defaultValue="female"
                            name={field.name}
                            value={field.value}
                            onChange={e => field.onChange(e)}
                        >
                            {options.map(o => (
                                <FormControlLabel
                                    value={o.value}
                                    control={<Radio />}
                                    label={
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontSize: 14,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {o.display}
                                        </Typography>
                                    }
                                    disabled={readonly}
                                    key={o.value.toString()}
                                />
                            ))}
                        </RadioGroup>
                        {error && !isTouched && (
                            <FormHelperText>{error.message}</FormHelperText>
                        )}
                    </>
                )}
            />
        </FormControl>
    );
}
